(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-mbb-destinations]';
  const VIEWPORT_SELECTOR = '[data-mbb-destinations-viewport]';
  const TRACK_SELECTOR = '[data-mbb-destinations-track]';
  const CARD_SELECTOR = '[data-mbb-destination-card]';
  const PREV_SELECTOR = '[data-mbb-destinations-prev]';
  const NEXT_SELECTOR = '[data-mbb-destinations-next]';
  const PAGINATION_SELECTOR = '[data-mbb-destinations-pagination]';
  const PROGRESS_SELECTOR = '[data-mbb-destinations-progress]';
  const REVEAL_SELECTOR = '[data-mbb-reveal]';
  const MODAL_OPEN_SELECTOR = '[data-mbb-destination-modal-open]';
  const MODAL_CLOSE_SELECTOR = '[data-mbb-destination-modal-close]';
  const MODAL_SELECTOR = '[data-mbb-destination-modal]';
  const MODAL_DIALOG_SELECTOR = '.mbb-home-destinations__modal-dialog';
  const MODAL_EMAIL_SELECTOR = '[data-mbb-modal-email]';
  const MODAL_DESTINATION_SELECTOR = '[data-mbb-modal-destination-name]';
  const MODAL_STATUS_SELECTOR = '[data-mbb-modal-destination-status]';
  const MODAL_DESTINATION_INPUT_SELECTOR = '[data-mbb-modal-destination-input]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  const instances = new WeakMap();
  let activeModalInstance = null;

  class MbbDestinations {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector(VIEWPORT_SELECTOR);
      this.track = section.querySelector(TRACK_SELECTOR);
      this.cards = [...section.querySelectorAll(CARD_SELECTOR)];
      this.previousButton = section.querySelector(PREV_SELECTOR);
      this.nextButton = section.querySelector(NEXT_SELECTOR);
      this.pagination = section.querySelector(PAGINATION_SELECTOR);
      this.progress = section.querySelector(PROGRESS_SELECTOR);
      this.modal = section.querySelector(MODAL_SELECTOR);
      this.modalDialog = section.querySelector(MODAL_DIALOG_SELECTOR);
      this.modalOpenButtons = [...section.querySelectorAll(MODAL_OPEN_SELECTOR)];
      this.modalCloseButtons = [...section.querySelectorAll(MODAL_CLOSE_SELECTOR)];
      this.modalEmail = section.querySelector(MODAL_EMAIL_SELECTOR);
      this.modalDestination = section.querySelector(MODAL_DESTINATION_SELECTOR);
      this.modalStatus = section.querySelector(MODAL_STATUS_SELECTOR);
      this.modalDestinationInput = section.querySelector(MODAL_DESTINATION_INPUT_SELECTOR);
      this.lastFocusedElement = null;
      this.inertItems = [];
      this.previousHtmlOverflow = '';
      this.previousBodyOverflow = '';
      this.isModalOpen = false;
      this.closeTimer = null;
      this.currentIndex = 0;
      this.visibleCards = 1;
      this.gap = 0;
      this.resizeTimer = null;

      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleModalOpen = this.handleModalOpen.bind(this);
      this.handleModalClose = this.handleModalClose.bind(this);
      this.handleModalKeydown = this.handleModalKeydown.bind(this);

      this.init();
    }

    init() {
      this.initReveal();
      this.bindModalEvents();

      if (!this.viewport || !this.track || !this.cards.length) return;

      this.bindEvents();
      this.updateLayout();
    }

    initReveal() {
      const elements = [...this.section.querySelectorAll(REVEAL_SELECTOR)];

      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)
      ) {
        elements.forEach((element) => element.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -40px'
        }
      );

      elements.forEach((element, index) => {
        element.style.transitionDelay = `${index * 80}ms`;
        observer.observe(element);
      });

      this.observer = observer;
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.viewport.addEventListener('scroll', this.handleScroll, {
        passive: true
      });
      window.addEventListener('resize', this.handleResize);
    }

    bindModalEvents() {
      this.modalOpenButtons.forEach((button) => {
        button.addEventListener('click', this.handleModalOpen);
      });

      this.modalCloseButtons.forEach((button) => {
        button.addEventListener('click', this.handleModalClose);
      });
    }

    getVisibleCards() {
      if (window.innerWidth <= 749) return 1;
      if (window.innerWidth <= 990) return 2;
      if (window.innerWidth <= 1200) return 3;
      return 5;
    }

    updateLayout() {
      this.visibleCards = this.getVisibleCards();
      this.gap = parseFloat(getComputedStyle(this.track).gap) || 0;

      const maxIndex = Math.max(0, this.cards.length - this.visibleCards);
      this.currentIndex = Math.min(this.currentIndex, maxIndex);
      this.buildPagination(maxIndex);

      this.updatePosition();
    }

    updatePosition() {
      if (window.innerWidth <= 749) {
        this.track.style.transform = '';
        const cardWidth = this.cards[0]?.getBoundingClientRect().width || 0;
        this.viewport.scrollTo({
          left: this.currentIndex * (cardWidth + this.gap),
          behavior: 'smooth',
        });
        this.updateControls();
        return;
      }

      const cardWidth = this.cards[0]?.getBoundingClientRect().width || 0;
      const offset = this.currentIndex * (cardWidth + this.gap);

      this.track.style.transform = `translate3d(-${offset}px,0,0)`;
      this.updateControls();
    }

    updateControls() {
      const maxIndex = Math.max(0, this.cards.length - this.visibleCards);

      if (this.previousButton) {
        this.previousButton.disabled = this.currentIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.currentIndex >= maxIndex;
      }

      this.pagination?.querySelectorAll('button').forEach((button, index) => {
        const active = index === this.currentIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.progress) {
        const progress =
          maxIndex === 0 ? 1 : (this.currentIndex + 1) / (maxIndex + 1);

        this.progress.style.transform = `scaleX(${progress})`;
      }
    }

    buildPagination(maxIndex) {
      if (!this.pagination) return;

      this.pagination.replaceChildren();
      const label = this.pagination.dataset.itemLabel || 'Destination';

      for (let index = 0; index <= maxIndex; index += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `${label} ${index + 1}`);
        button.addEventListener('click', () => {
          this.currentIndex = index;
          this.updatePosition();
        });
        this.pagination.appendChild(button);
      }
    }

    handlePrevious() {
      if (this.currentIndex <= 0) return;

      this.currentIndex -= 1;
      this.updatePosition();
    }

    handleNext() {
      const maxIndex = Math.max(0, this.cards.length - this.visibleCards);

      if (this.currentIndex >= maxIndex) return;

      this.currentIndex += 1;
      this.updatePosition();
    }

    handleScroll() {
      if (window.innerWidth > 749) return;

      const card = this.cards[0];
      if (!card) return;

      const cardWidth = card.getBoundingClientRect().width + this.gap;
      this.currentIndex = Math.round(this.viewport.scrollLeft / cardWidth);
      this.updateControls();
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.updateLayout();
      }, 150);
    }

    handleModalOpen(event) {
      event.preventDefault();
      this.openModal(event.currentTarget);
    }

    handleModalClose(event) {
      event?.preventDefault();
      this.closeModal();
    }

    handleModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key !== 'Tab' || !this.modalDialog) return;

      const focusableElements = this.getModalFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        this.modalDialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    openModal(trigger) {
      if (!this.modal || !trigger) return;

      if (activeModalInstance && activeModalInstance !== this) {
        activeModalInstance.closeModal({ restoreFocus: false });
      }

      const title = trigger.dataset.destinationTitle || '';
      const status = trigger.dataset.destinationStatus || '';
      const fallbackTitle = title || trigger.textContent.trim();
      const destinationNote = [fallbackTitle, status].filter(Boolean).join(' — ');

      this.lastFocusedElement = trigger;

      if (this.modalDestination) {
        this.modalDestination.textContent = fallbackTitle;
      }

      if (this.modalStatus) {
        this.modalStatus.textContent = status;
      }

      if (this.modalDestinationInput) {
        this.modalDestinationInput.value = destinationNote
          ? `${this.section.dataset.requestPrefix} ${destinationNote}`
          : '';
      }

      window.clearTimeout(this.closeTimer);
      this.modal.hidden = false;
      this.modal.removeAttribute('hidden');
      this.previousHtmlOverflow = document.documentElement.style.overflow;
      this.previousBodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.setBackgroundInert(true);

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      this.isModalOpen = true;
      activeModalInstance = this;
      document.addEventListener('keydown', this.handleModalKeydown);

      window.requestAnimationFrame(() => {
        if (!this.isModalOpen) return;
        (this.modalEmail || this.modalDialog)?.focus();
      });
    }

    closeModal({ restoreFocus = true } = {}) {
      if (!this.modal || !this.isModalOpen) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      this.isModalOpen = false;
      this.closeTimer = window.setTimeout(() => {
        if (!this.isModalOpen && this.modal) {
          this.modal.hidden = true;
        }
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300);
      document.removeEventListener('keydown', this.handleModalKeydown);
      document.documentElement.style.overflow = this.previousHtmlOverflow;
      document.body.style.overflow = this.previousBodyOverflow;
      this.setBackgroundInert(false);

      if (activeModalInstance === this) {
        activeModalInstance = null;
      }

      if (restoreFocus && this.lastFocusedElement?.isConnected) {
        this.lastFocusedElement.focus();
      }

      this.lastFocusedElement = null;
    }

    setBackgroundInert(isInert) {
      if (!this.modal || !this.section.children.length) return;

      if (isInert) {
        this.inertItems = [...this.section.children]
          .filter((element) => element !== this.modal)
          .map((element) => ({
            element,
            ariaHidden: element.getAttribute('aria-hidden'),
            inert: element.inert
          }));

        this.inertItems.forEach(({ element }) => {
          element.setAttribute('aria-hidden', 'true');
          if ('inert' in element) element.inert = true;
        });

        return;
      }

      this.inertItems.forEach(({ element, ariaHidden, inert }) => {
        if (ariaHidden === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', ariaHidden);
        }

        if ('inert' in element) element.inert = inert;
      });

      this.inertItems = [];
    }

    getModalFocusableElements() {
      if (!this.modalDialog) return [];

      return [...this.modalDialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => element.offsetParent !== null || element === document.activeElement
      );
    }

    destroy() {
      this.closeModal({ restoreFocus: false });
      this.modalOpenButtons.forEach((button) => {
        button.removeEventListener('click', this.handleModalOpen);
      });
      this.modalCloseButtons.forEach((button) => {
        button.removeEventListener('click', this.handleModalClose);
      });
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      this.observer?.disconnect();
      window.clearTimeout(this.resizeTimer);
      window.clearTimeout(this.closeTimer);
      instances.delete(this.section);
    }
  }

  function initSection(section) {
    if (!section || instances.has(section)) return;

    instances.set(section, new MbbDestinations(section));
  }

  function initAll(scope = document) {
    if (scope.matches?.(SECTION_SELECTOR)) {
      initSection(scope);
    }

    scope.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
  }

  function destroySection(section) {
    instances.get(section)?.destroy();
  }

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section =
      event.target.matches?.(SECTION_SELECTOR)
        ? event.target
        : event.target.querySelector?.(SECTION_SELECTOR);

    if (section) destroySection(section);
  });

  document.addEventListener('shopify:block:select', (event) => {
    event.target
      .closest(REVEAL_SELECTOR)
      ?.classList.add('is-visible');
  });
})();
